/**
 * ForceRenderer — the force-directed network lens.
 *
 * Renders a GraphData object as a D3 force-directed graph in a selectable
 * theme. The dark "constellation" theme is the Hooktheory-derived language
 * (near-black field, white opacity-weighted edges, white glow halos); the
 * editorial theme is a light, print-inspired variant (warm field, ink edges,
 * colored halos, hairline borders). Both read from a single Theme object.
 *
 * Five node encoding dimensions are live in Phase 1:
 *   size    ← node.value      (sqrt scale)
 *   color   ← node.cluster    (dynamic 7-color palette, rest neutral)
 *   opacity ← node.confidence (band)
 *   glow    ← node.status     (off / tier1 / tier2 / tier3)
 *   pattern ← node.clusters   (diagonal stripe when bridging clusters)
 */

import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
  forceX,
  forceY,
  type Simulation,
  type SimulationNodeDatum,
  type SimulationLinkDatum,
} from "d3-force";
import { scaleSqrt, scaleLinear } from "d3-scale";
import { select, type Selection } from "d3-selection";

import type { GraphData, NodeSpec, EdgeSpec, ForceParams } from "./types.js";
import { THEMES, DEFAULT_FORCES, type Theme, type ThemeName } from "./theme.js";

interface SimNode extends NodeSpec, SimulationNodeDatum {}
interface SimLink extends SimulationLinkDatum<SimNode> {
  weight: number;
  flow?: number;
  type?: EdgeSpec["type"];
  temporal?: EdgeSpec["temporal"];
}

export interface ForceRendererOptions {
  width?: number;
  height?: number;
  forces?: ForceParams;
  /** Theme name or a full Theme object. Defaults to "dark". */
  theme?: ThemeName | Theme;
  /** Render foreignObject labels. */
  showLabels?: boolean;
  /** Only label nodes whose radius is at least this many px (reduces clutter). */
  labelMinRadius?: number;
}

const SVG_NS = "http://www.w3.org/2000/svg";

export class ForceRenderer {
  private readonly svg: Selection<SVGSVGElement, unknown, null, undefined>;
  private readonly width: number;
  private readonly height: number;
  private readonly theme: Theme;
  private forces: Required<ForceParams>;
  private readonly showLabels: boolean;
  private readonly labelMinRadius: number;

  private simulation: Simulation<SimNode, SimLink> | null = null;
  private content: Selection<SVGGElement, unknown, null, undefined> | null = null;
  private nodes: SimNode[] = [];
  private links: SimLink[] = [];
  private radiusOf: (n: SimNode) => number = () => 6;
  private colorOf: (n: SimNode) => string = () => "#888";

  constructor(
    container: SVGSVGElement | HTMLElement,
    options: ForceRendererOptions = {},
  ) {
    this.width = options.width ?? 960;
    this.height = options.height ?? 720;
    this.theme =
      typeof options.theme === "object"
        ? options.theme
        : THEMES[options.theme ?? "dark"];
    this.showLabels = options.showLabels ?? true;
    this.labelMinRadius = options.labelMinRadius ?? 10;
    this.forces = { ...DEFAULT_FORCES, ...options.forces };

    if (container instanceof SVGSVGElement) {
      this.svg = select(container);
    } else {
      const svgEl = document.createElementNS(SVG_NS, "svg");
      container.appendChild(svgEl);
      this.svg = select(svgEl as SVGSVGElement);
    }

    this.svg
      .attr("width", this.width)
      .attr("height", this.height)
      .attr("viewBox", `0 0 ${this.width} ${this.height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("background", this.theme.background);
  }

  /** Render a graph. Replaces any previously rendered graph. */
  render(data: GraphData): this {
    this.svg.selectAll("*").remove();

    // Background rect so exported SVGs carry the field with them.
    this.svg
      .append("rect")
      .attr("width", this.width)
      .attr("height", this.height)
      .attr("fill", this.theme.background);

    this.nodes = data.nodes.map((n) => ({ ...n }));
    const nodeById = new Map(this.nodes.map((n) => [n.id, n]));
    this.links = data.edges
      .filter((e) => nodeById.has(e.source) && nodeById.has(e.target))
      .map((e) => ({
        source: e.source,
        target: e.target,
        weight: e.weight,
        flow: e.flow,
        type: e.type,
        temporal: e.temporal,
      }));

    this.buildScales();
    this.buildDefs();

    // All drawn geometry lives in one content group so fit-to-view can frame it.
    this.content = this.svg.append("g").attr("class", "content");

    const linkSel = this.drawEdges();
    const { nodeSel, labelSel } = this.drawNodes();

    this.simulation = this.buildSimulation();
    this.simulation.on("tick", () => {
      linkSel
        .attr("x1", (d) => (d.source as SimNode).x ?? 0)
        .attr("y1", (d) => (d.source as SimNode).y ?? 0)
        .attr("x2", (d) => (d.target as SimNode).x ?? 0)
        .attr("y2", (d) => (d.target as SimNode).y ?? 0);

      nodeSel.attr("transform", (d) => `translate(${d.x ?? 0},${d.y ?? 0})`);
      labelSel
        ?.attr("x", (d) => (d.x ?? 0) + this.radiusOf(d) + 2)
        .attr("y", (d) => (d.y ?? 0) - this.radiusOf(d));
    });
    // Frame the layout once it cools (and after force tweaks re-settle it).
    this.simulation.on("end", () => this.fit());

    return this;
  }

  /** Update force parameters and gently reheat the simulation. */
  setForces(params: ForceParams): this {
    this.forces = { ...this.forces, ...params };
    if (!this.simulation) return this;

    this.simulation
      .force<ReturnType<typeof forceLink<SimNode, SimLink>>>("link")
      ?.distance(this.forces.linkDistance)
      .strength((d) => this.forces.linkStrength * normWeight(d.weight));
    this.simulation
      .force<ReturnType<typeof forceManyBody>>("charge")
      ?.strength(this.forces.charge);
    this.simulation
      .force<ReturnType<typeof forceX<SimNode>>>("gravityX")
      ?.strength(this.forces.centerGravity);
    this.simulation
      .force<ReturnType<typeof forceY<SimNode>>>("gravityY")
      ?.strength(this.forces.centerGravity);
    this.simulation.alpha(0.5).restart();
    return this;
  }

  /**
   * Run the simulation to a settled layout synchronously, without animation.
   * Useful for headless SVG export (Phase 2 / Claude Code CLI path).
   */
  settle(ticks = 300): this {
    if (!this.simulation) return this;
    this.simulation.stop();
    this.simulation.tick(ticks);
    this.simulation.on("tick")?.call(this.simulation);
    this.fit();
    return this;
  }

  /**
   * Scale + translate the content group so the settled layout fills the
   * viewport with padding. A force layout's absolute size is arbitrary, so this
   * is what actually frames the graph; the scale is capped so a tiny graph
   * doesn't balloon and a dense one stays legible.
   */
  private fit(padding = 80): void {
    if (!this.content || this.nodes.length === 0) return;
    const fs = this.theme.label.fontSize;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of this.nodes) {
      const r = this.radiusOf(n) * (n.status ? this.theme.glow.outerScale : 1);
      minX = Math.min(minX, (n.x ?? 0) - r);
      minY = Math.min(minY, (n.y ?? 0) - r);
      maxX = Math.max(maxX, (n.x ?? 0) + r);
      maxY = Math.max(maxY, (n.y ?? 0) + r);
      // Labels sit up-and-right of the node, so account for their box too.
      if (this.showLabels && this.radiusOf(n) >= this.labelMinRadius) {
        const textW = (n.label.length + String(n.value).length + 3) * fs * 0.72;
        maxX = Math.max(maxX, (n.x ?? 0) + this.radiusOf(n) + 2 + textW);
        minY = Math.min(minY, (n.y ?? 0) - this.radiusOf(n) - fs);
      }
    }
    const bw = Math.max(maxX - minX, 1);
    const bh = Math.max(maxY - minY, 1);
    const scale = Math.max(
      0.5,
      Math.min(1.45, (this.width - 2 * padding) / bw, (this.height - 2 * padding) / bh),
    );
    const tx = this.width / 2 - scale * (minX + maxX) / 2;
    const ty = this.height / 2 - scale * (minY + maxY) / 2;
    this.content.attr("transform", `translate(${tx},${ty}) scale(${scale})`);
  }

  /** Serialize the current SVG to a standalone string. */
  exportSVG(): string {
    const node = this.svg.node();
    if (!node) return "";
    const clone = node.cloneNode(true) as SVGSVGElement;
    // XMLSerializer emits the SVG namespace itself; setting it here too would
    // produce a duplicate xmlns attribute and break strict XML parsers.
    return new XMLSerializer().serializeToString(clone);
  }

  destroy(): void {
    this.simulation?.stop();
    this.svg.selectAll("*").remove();
  }

  // ── internals ─────────────────────────────────────────────────────────

  private buildScales(): void {
    const values = this.nodes.map((n) => n.value);
    const minV = Math.min(...values, 0);
    const maxV = Math.max(...values, 1);
    const radius = scaleSqrt()
      .domain([minV, maxV])
      .range([this.theme.node.radiusMin, this.theme.node.radiusMax]);
    this.radiusOf = (n) => radius(n.value);

    // Dynamic palette: rank clusters by total value, color the top N, rest neutral.
    const weightByCluster = new Map<string, number>();
    for (const n of this.nodes) {
      weightByCluster.set(
        n.cluster,
        (weightByCluster.get(n.cluster) ?? 0) + n.value,
      );
    }
    const ranked = [...weightByCluster.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, this.theme.maxColoredClusters)
      .map(([cluster]) => cluster);
    const clusterColor = new Map<string, string>();
    ranked.forEach((cluster, i) =>
      clusterColor.set(cluster, this.theme.palette[i]),
    );
    this.colorOf = (n) =>
      clusterColor.get(n.cluster) ?? this.theme.neutralCluster;
  }

  private edgeOpacityScale: ((w: number) => number) | null = null;
  private edgeOpacity(weight: number, weights: number[]): number {
    if (!this.edgeOpacityScale) {
      const min = Math.min(...weights, 0);
      const max = Math.max(...weights, 1);
      this.edgeOpacityScale = scaleLinear()
        .domain([min, max])
        .range([this.theme.edge.opacityMin, this.theme.edge.opacityMax])
        .clamp(true);
    }
    return this.edgeOpacityScale(weight);
  }

  private buildDefs(): void {
    const defs = this.svg.append("defs");

    for (const tier of [1, 2, 3] as const) {
      const filter = defs
        .append("filter")
        .attr("id", `glow-blur-${tier}`)
        .attr("x", "-75%")
        .attr("y", "-75%")
        .attr("width", "250%")
        .attr("height", "250%");
      filter
        .append("feGaussianBlur")
        .attr("in", "SourceGraphic")
        .attr("stdDeviation", this.theme.glow.blurByTier[tier]);
    }

    const glowColors = new Set<string>();
    for (const n of this.nodes) if (n.status) glowColors.add(this.colorOf(n));
    for (const color of glowColors) {
      const grad = defs
        .append("radialGradient")
        .attr("id", gradId(color))
        .attr("cx", "50%")
        .attr("cy", "50%")
        .attr("r", "50%");
      grad
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", color)
        .attr("stop-opacity", 0.9);
      grad
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", color)
        .attr("stop-opacity", 0);
    }

    const stripeColors = new Set<string>();
    for (const n of this.nodes) if (isBridging(n)) stripeColors.add(this.colorOf(n));
    for (const color of stripeColors) {
      const size = this.theme.pattern.size;
      const pattern = defs
        .append("pattern")
        .attr("id", patternId(color))
        .attr("width", size)
        .attr("height", size)
        .attr("patternUnits", "userSpaceOnUse");
      pattern
        .append("rect")
        .attr("width", size)
        .attr("height", size)
        .attr("fill", color);
      pattern
        .append("line")
        .attr("x1", 0)
        .attr("y1", 0)
        .attr("x2", size)
        .attr("y2", size)
        .attr("stroke", this.theme.pattern.strokeColor)
        .attr("stroke-width", 1);
    }
  }

  private drawEdges(): Selection<SVGLineElement, SimLink, SVGGElement, unknown> {
    const weights = this.links.map((l) => l.weight);
    const flows = this.links.map((l) => l.flow ?? l.weight);
    const minF = Math.min(...flows, 0);
    const maxF = Math.max(...flows, 1);
    const width = scaleLinear()
      .domain([minF, maxF])
      .range([this.theme.edge.widthMin, this.theme.edge.widthMax])
      .clamp(true);

    const g = (this.content ?? this.svg).append("g").attr("class", "edges");
    return g
      .selectAll<SVGLineElement, SimLink>("line")
      .data(this.links)
      .join("line")
      .attr("stroke", (d) =>
        d.type
          ? this.theme.edge.colorByType[d.type] ?? this.theme.edge.stroke
          : this.theme.edge.stroke,
      )
      .attr("stroke-opacity", (d) => this.edgeOpacity(d.weight, weights))
      .attr("stroke-width", (d) => width(d.flow ?? d.weight))
      .attr("stroke-dasharray", (d) => dashFor(d.temporal));
  }

  private drawNodes(): {
    nodeSel: Selection<SVGGElement, SimNode, SVGGElement, unknown>;
    labelSel: Selection<SVGTextElement, SimNode, SVGGElement, unknown> | null;
  } {
    const theme = this.theme;
    const parent = this.content ?? this.svg;
    const g = parent.append("g").attr("class", "nodes");
    const nodeSel = g
      .selectAll<SVGGElement, SimNode>("g.node")
      .data(this.nodes)
      .join("g")
      .attr("class", "node");

    nodeSel.each((d, i, groups) => {
      const node = select(groups[i]);
      const r = this.radiusOf(d);
      const color = this.colorOf(d);
      const tier = glowTier(d);

      // Three-tier glow: outer halo → colored ring → solid core.
      if (tier > 0) {
        const outerFill =
          theme.glow.outerColor === "white"
            ? `rgba(255,255,255,${theme.glow.haloOpacity})`
            : withAlpha(color, theme.glow.haloOpacity);
        node
          .append("circle")
          .attr("r", r * theme.glow.outerScale)
          .attr("fill", outerFill)
          .attr("filter", `url(#glow-blur-${tier})`);
        node
          .append("circle")
          .attr("r", r * theme.glow.middleScale)
          .attr("fill", `url(#${gradId(color)})`);
      }

      // Solid identity core (or stripe pattern when bridging clusters).
      const core = node
        .append("circle")
        .attr("r", r)
        .attr("fill", isBridging(d) ? `url(#${patternId(color)})` : color)
        .attr("fill-opacity", nodeOpacity(d, theme))
        .attr("class", d.status === "active" ? "node-core pulse" : "node-core");

      // Hairline border (editorial) and/or crisp accent ring for status nodes.
      if (tier > 0 && theme.glow.ring) {
        core
          .attr("stroke", theme.glow.ring.color)
          .attr("stroke-width", theme.glow.ring.widthByTier[tier]);
      } else if (theme.node.stroke) {
        core
          .attr("stroke", theme.node.stroke)
          .attr("stroke-width", theme.node.strokeWidth ?? 1);
      }
    });

    let labelSel:
      | Selection<SVGTextElement, SimNode, SVGGElement, unknown>
      | null = null;
    if (this.showLabels) {
      const labelG = parent.append("g").attr("class", "labels");
      const labelled = this.nodes.filter(
        (n) => this.radiusOf(n) >= this.labelMinRadius,
      );
      labelSel = labelG
        .selectAll<SVGTextElement, SimNode>("text")
        .data(labelled)
        .join("text")
        .attr("font-family", theme.label.fontFamily)
        .attr("font-size", theme.label.fontSize)
        .attr("fill", theme.label.color)
        // Background-colored halo keeps labels legible where they cross nodes.
        .attr("paint-order", "stroke")
        .attr("stroke", theme.background)
        .attr("stroke-width", 2.5)
        .attr("stroke-linejoin", "round")
        .each((d, i, groups) => {
          const t = select(groups[i]);
          t.append("tspan").text(d.label);
          t.append("tspan")
            .attr("fill", theme.label.valueColor)
            .attr("font-family", theme.label.valueFontFamily ?? theme.label.fontFamily)
            .attr("dx", 4)
            .text(formatValue(d.value));
        });
    }

    return { nodeSel, labelSel };
  }

  private buildSimulation(): Simulation<SimNode, SimLink> {
    const sim = forceSimulation<SimNode>(this.nodes)
      .force(
        "link",
        forceLink<SimNode, SimLink>(this.links)
          .id((d) => d.id)
          .distance(this.forces.linkDistance)
          .strength((d) => this.forces.linkStrength * normWeight(d.weight)),
      )
      .force("charge", forceManyBody().strength(this.forces.charge))
      // forceCenter keeps the graph's center of mass in the viewport;
      // forceX/forceY add the tunable gravity that pulls nodes inward.
      .force("center", forceCenter(this.width / 2, this.height / 2))
      .force("gravityX", forceX(this.width / 2).strength(this.forces.centerGravity))
      .force("gravityY", forceY(this.height / 2).strength(this.forces.centerGravity))
      .force(
        "collide",
        forceCollide<SimNode>((d) => this.radiusOf(d) + 2),
      );

    sim.force("cluster", this.clusterForce());
    return sim;
  }

  /** Custom force: same-cluster nodes attract toward their shared centroid. */
  private clusterForce() {
    const strength = this.forces.clusterGravity;
    let nodes: SimNode[] = [];
    const force = (alpha: number) => {
      const centroids = new Map<string, { x: number; y: number; n: number }>();
      for (const node of nodes) {
        const c = centroids.get(node.cluster) ?? { x: 0, y: 0, n: 0 };
        c.x += node.x ?? 0;
        c.y += node.y ?? 0;
        c.n += 1;
        centroids.set(node.cluster, c);
      }
      const k = strength * alpha;
      for (const node of nodes) {
        const c = centroids.get(node.cluster);
        if (!c || c.n === 0) continue;
        node.vx = (node.vx ?? 0) + (c.x / c.n - (node.x ?? 0)) * k;
        node.vy = (node.vy ?? 0) + (c.y / c.n - (node.y ?? 0)) * k;
      }
    };
    force.initialize = (n: SimNode[]) => {
      nodes = n;
    };
    return force;
  }
}

// ── pure helpers ──────────────────────────────────────────────────────────

function glowTier(n: NodeSpec): 0 | 1 | 2 | 3 {
  switch (n.status) {
    case "trending":
      return 3;
    case "hub":
      return 2;
    case "gap":
    case "active":
      return 1;
    default:
      return 0;
  }
}

function nodeOpacity(n: NodeSpec, theme: Theme): number {
  if (n.confidence === undefined) return theme.node.opacity;
  const { opacityMin, opacityMax } = theme.node;
  return opacityMin + (opacityMax - opacityMin) * clamp01(n.confidence);
}

function isBridging(n: NodeSpec): boolean {
  return Array.isArray(n.clusters) && n.clusters.length > 1;
}

function dashFor(temporal: EdgeSpec["temporal"]): string | null {
  switch (temporal) {
    case "recent":
      return "4 2";
    case "historical":
      return "1 3";
    default:
      return null;
  }
}

/** Weights arrive on many scales; squash into 0–1 so link strength stays sane. */
function normWeight(w: number): number {
  return clamp01(w > 1 ? w / 100 : w);
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function formatValue(v: number): string {
  if (Number.isInteger(v)) return String(v);
  return v < 1 ? `${Math.round(v * 100)}%` : v.toFixed(1);
}

/** Add an alpha channel to a #rrggbb color → rgba(). Falls back to the input. */
function withAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

const slug = (color: string) =>
  color.replace("#", "c").replace(/[^a-zA-Z0-9]/g, "");
const gradId = (color: string) => `glow-grad-${slug(color)}`;
const patternId = (color: string) => `stripe-${slug(color)}`;
